import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import axios from "axios";
import SiteFooter from "../components/SiteFooter";
import { CityAutocomplete } from "../components/CityAutocomplete";

const Global = createGlobalStyle`
  @import url('https://cdn.jsdelivr.net/gh/orioncactus/pretendard/dist/web/static/pretendard.css');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: #f0ebe3; font-family: 'Pretendard', 'Malgun Gothic', sans-serif; }
`;

const API_BASE = process.env.REACT_APP_API_URL || "";

function parseTime(val: string) {
  if (!val.trim()) return { hour: 99, minute: 0 };
  const parts = val.split(":");
  const h = parseInt(parts[0]);
  const m = parseInt(parts[1] || "0");
  return {
    hour: (!isNaN(h) && h >= 0 && h <= 23) ? h : 99,
    minute: (!isNaN(m) && m >= 0 && m <= 59) ? m : 0,
  };
}

function parseBirthDate(val: string) {
  const sep = val.includes("/") ? "/" : val.includes(".") ? "." : val.includes("-") ? "-" : null;
  if (sep) { const [y,m,d] = val.split(sep).map(Number); return { year:y||0,month:m||0,day:d||0 }; }
  if (val.length === 8) return { year:Number(val.slice(0,4)),month:Number(val.slice(4,6)),day:Number(val.slice(6,8)) };
  return { year:0,month:0,day:0 };
}

/* ── 서비스 정의 ──────────────────────────────────────────── */
const SERVICES = [
  {
    key: "퀵사주_연애운",
    label: "연애운",
    icon: "💕",
    price: 1000,
    color: "#e03e7c",
    desc: "이상형을 어디서 만날까?\n어떻게 생겼을까? 언제 만날까?",
    needPartner: false,
  },
  {
    key: "퀵사주_직장운",
    label: "직장운",
    icon: "💼",
    price: 1000,
    color: "#2563eb",
    desc: "이직은 언제?\n재직 중이라면 승진할까?",
    needPartner: false,
  },
  {
    key: "퀵사주_재물운",
    label: "재물운",
    icon: "💰",
    price: 1000,
    color: "#059669",
    desc: "투자 성공할까?\n계약운, 사업 매출은?",
    needPartner: false,
  },
  {
    key: "퀵사주_연애궁합",
    label: "연애궁합",
    icon: "💑",
    price: 2000,
    color: "#7c3aed",
    desc: "우리의 궁합점수는?\n성격과 속궁합은 맞을까?",
    needPartner: true,
    ageLimit: true,
  },
];


const BIRTH_TYPES = [
  { value: "solar",      label: "양력" },
  { value: "lunar",      label: "음력" },
  { value: "lunar_leap", label: "음력 윤달" },
];

/* ── styled components ──────────────────────────────────────── */
const Wrap = styled.div`
  max-width: 560px;
  margin: 0 auto;
  padding: 0 0 80px;
`;

const NavBar = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 16px 0;
  margin-bottom: 4px;
`;

const NavBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.7);
  border: 1.5px solid #d8cfe8;
  border-radius: 20px;
  padding: 7px 14px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  color: #5a4a72;
  font-family: inherit;
  box-shadow: 0 2px 8px rgba(74,32,96,0.08);
  & + & { margin-left: 8px; }
`;

const PageTitle = styled.div`
  font-size: 15px;
  font-weight: 900;
  color: #4a2060;
  margin-left: auto;
`;

const HeaderBox = styled.div`
  text-align: center;
  padding: 20px 16px 24px;
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── 서비스 카드 ─────────────────────────────────────────────── */
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding: 0 16px;
`;

const SvcCard = styled.button<{ $color: string; $idx: number }>`
  background: #fff;
  border: 2px solid ${p => p.$color}28;
  border-radius: 18px;
  padding: 20px 16px 18px;
  cursor: pointer;
  text-align: left;
  box-shadow: 0 4px 16px rgba(74,32,96,0.07);
  font-family: inherit;
  position: relative;
  overflow: hidden;
  animation: ${fadeUp} 0.35s ease both;
  animation-delay: ${p => p.$idx * 0.07}s;
  transition: transform 0.15s, box-shadow 0.15s;

  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 3px;
    background: ${p => p.$color};
    border-radius: 18px 18px 0 0;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 28px ${p => p.$color}22;
  }
`;

const SvcIcon = styled.div`font-size: 1.8rem; margin-bottom: 8px;`;

const SvcLabel = styled.div<{ $color: string }>`
  font-size: 11px;
  font-weight: 600;
  color: ${p => p.$color};
  margin-bottom: 4px;
  opacity: 0.75;
`;

const SvcDesc = styled.div<{ $color: string }>`
  font-size: 13.5px;
  font-weight: 800;
  color: #2d1f3d;
  line-height: 1.6;
  white-space: pre-line;
  margin-bottom: 14px;
`;

const SvcPrice = styled.div<{ $color: string }>`
  display: inline-block;
  background: ${p => p.$color}14;
  color: ${p => p.$color};
  border-radius: 20px;
  padding: 5px 12px;
  font-size: 11px;
  font-weight: 700;
`;

/* ── 입력 폼 ──────────────────────────────────────────────────── */
const FormArea = styled.div`padding: 0 16px;`;

const ServiceBadge = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 10px;
  background: ${p => p.$color}10;
  border: 1.5px solid ${p => p.$color}30;
  border-radius: 14px;
  padding: 12px 16px;
  margin-bottom: 18px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 20px 20px 16px;
  box-shadow: 0 4px 24px rgba(74,32,96,0.10);
  margin-bottom: 14px;
`;

const CardTopBar = styled.div<{ $color: string }>`
  font-size: 13px;
  font-weight: 800;
  color: ${p => p.$color};
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  &::before {
    content: '';
    display: inline-block;
    width: 4px;
    height: 14px;
    background: ${p => p.$color};
    border-radius: 2px;
  }
`;

const SectionTitle = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #9c7b5a;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 16px 0 8px;
  &:first-child { margin-top: 0; }
`;

const FieldRow = styled.div`display: flex; gap: 8px; margin-bottom: 10px;`;
const Field = styled.div`flex: 1; display: flex; flex-direction: column; gap: 4px;`;
const Label = styled.label`font-size: 12px; color: #5a4a3a; font-weight: 600;`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e0d5c8;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #3d2b1f;
  background: #fdfaf7;
  font-family: inherit;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: #c09a6b; }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid #e0d5c8;
  border-radius: 10px;
  font-size: 0.9rem;
  color: #3d2b1f;
  background: #fdfaf7;
  font-family: inherit;
  &:focus { outline: none; border-color: #c09a6b; }
`;

const PartnerDivider = styled.div`
  border-top: 2px dashed #e0d5c8;
  margin: 16px -20px 0;
  padding: 16px 20px 0;
  background: linear-gradient(180deg, #f8f9fa 0%, #fff 100%);
`;

const ConsentBox = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.8rem;
  color: #5a4a3a;
  line-height: 1.5;
  margin: 18px 0 0;
  cursor: pointer;
  input { margin-top: 2px; accent-color: #c09a6b; width: 16px; height: 16px; flex-shrink: 0; }
`;

const ErrMsg = styled.div`
  padding: 10px 14px;
  background: #fff5f8;
  border: 1px solid #f5c2cf;
  border-radius: 10px;
  font-size: 13px;
  color: #c0392b;
  margin-top: 12px;
`;

const SubmitBtn = styled.button<{ $color: string; $disabled?: boolean }>`
  width: 100%;
  padding: 15px;
  background: ${p => p.$disabled ? "#ccc" : `linear-gradient(135deg, ${p.$color}, ${p.$color}cc)`};
  color: #fff;
  border: none;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 800;
  cursor: ${p => p.$disabled ? "not-allowed" : "pointer"};
  font-family: inherit;
  box-shadow: ${p => p.$disabled ? "none" : `0 4px 18px ${p.$color}44`};
  margin-top: 16px;
  transition: opacity 0.2s;
  &:hover { opacity: ${p => p.$disabled ? 1 : 0.92}; }
`;

/* ── 결과 화면 ──────────────────────────────────────────────── */
const ResultCard = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 32px 24px;
  box-shadow: 0 4px 24px rgba(74,32,96,0.10);
  text-align: center;
  margin: 0 16px;
`;

/* ── 인터페이스 ─────────────────────────────────────────────── */
interface PersonForm {
  name: string;
  gender: string;
  birth_type: string;
  birth_date: string;
  birth_time: string;
  birth_time_unknown: boolean;
  city: string;
}

const emptyPerson = (): PersonForm => ({
  name: "", gender: "female", birth_type: "solar",
  birth_date: "",
  birth_time: "",
  birth_time_unknown: false,
  city: "서울",
});

/* ── PersonFormBlock (모듈 레벨에 정의 — 성능 핵심) ─────────── */
interface PFBProps {
  title: string;
  data: PersonForm;
  onChange: (d: PersonForm) => void;
  accentColor: string;
  email?: string;
  onEmailChange?: (v: string) => void;
}

function PersonFormBlock({ title, data, onChange, accentColor, email, onEmailChange }: PFBProps) {
  return (
    <Card>
      <CardTopBar $color={accentColor}>{title}</CardTopBar>

      <SectionTitle>기본 정보</SectionTitle>
      <FieldRow>
        <Field>
          <Label>이름 *</Label>
          <Input
            placeholder="홍길동"
            value={data.name}
            onChange={e => onChange({ ...data, name: e.target.value })}
            inputMode="text"
            autoComplete="name"
          />
        </Field>
        <Field style={{ maxWidth: 110 }}>
          <Label>성별 *</Label>
          <Select value={data.gender} onChange={e => onChange({ ...data, gender: e.target.value })}>
            <option value="female">여성</option>
            <option value="male">남성</option>
          </Select>
        </Field>
      </FieldRow>

      {onEmailChange !== undefined && (
        <FieldRow>
          <Field>
            <Label>결과 받을 이메일 *</Label>
            <Input
              type="email"
              placeholder="example@email.com"
              value={email ?? ""}
              onChange={e => onEmailChange(e.target.value)}
              inputMode="email"
              autoComplete="email"
            />
            <div style={{ fontSize: 11, color: "#9a8aaa", marginTop: 3 }}>
              결제 완료 후 이메일로 결과지가 발송됩니다
            </div>
          </Field>
        </FieldRow>
      )}

      <SectionTitle>생년월일시</SectionTitle>
      <FieldRow>
        <Field>
          <Label>구분</Label>
          <Select value={data.birth_type} onChange={e => onChange({ ...data, birth_type: e.target.value })}>
            {BIRTH_TYPES.map(bt => (
              <option key={bt.value} value={bt.value}>{bt.label}</option>
            ))}
          </Select>
        </Field>
        <Field style={{ flex: 2 }}>
          <Label>생년월일 * (예: 1990/05/15)</Label>
          <Input
            placeholder="1990/05/15" maxLength={10}
            value={data.birth_date}
            onChange={e => onChange({ ...data, birth_date: e.target.value })}
          />
        </Field>
      </FieldRow>
      <FieldRow style={{ alignItems: "center", marginBottom: 4 }}>
        <Label style={{ marginBottom: 0 }}>태어난 시간</Label>
        <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: "0.78rem", color: "#7a6654", cursor: "pointer", marginLeft: 8 }}>
          <input type="checkbox" checked={data.birth_time_unknown}
            onChange={e => onChange({ ...data, birth_time_unknown: e.target.checked })}
            style={{ accentColor: "#c09a6b" }} />
          시간 모름
        </label>
      </FieldRow>
      <FieldRow>
        <Field>
          <Input
            placeholder="예: 12:50" maxLength={5}
            value={data.birth_time_unknown ? "" : data.birth_time}
            disabled={data.birth_time_unknown}
            style={data.birth_time_unknown ? { background: "#f5f0ea", color: "#b0a090", cursor: "not-allowed" } : {}}
            onChange={e => onChange({ ...data, birth_time: e.target.value })}
          />
        </Field>
      </FieldRow>

      <SectionTitle>지역 (시간 보정용)</SectionTitle>
      <FieldRow style={{ marginBottom: 0 }}>
        <Field>
          <CityAutocomplete
            value={data.city}
            onChange={v => onChange({ ...data, city: v })}
          />
        </Field>
      </FieldRow>
    </Card>
  );
}

/* ── QuickPage ───────────────────────────────────────────────── */
export default function QuickPage() {
  const navigate = useNavigate();
  const [step, setStep]     = useState<0 | 1>(0);
  const [svcKey, setSvcKey] = useState<string>("");
  const [person1, setPerson1] = useState<PersonForm>(emptyPerson());
  const [person2, setPerson2] = useState<PersonForm>(emptyPerson());
  const [email, setEmail]   = useState<string>("");
  const [consent, setConsent] = useState(false);
  const [err, setErr]       = useState<string>("");
  const [paying, setPaying] = useState(false);
  const [result, setResult] = useState<{
    emailSent: boolean;
    reportHtml: string;
    consultationId: number | null;
  } | null>(null);

  const svc = SERVICES.find(s => s.key === svcKey);

  function selectService(key: string) {
    setSvcKey(key);
    setPerson1(emptyPerson());
    setPerson2(emptyPerson());
    setEmail("");
    setConsent(false);
    setErr("");
    setResult(null);
    setStep(1);
  }

  function validateForm(): string | null {
    if (!consent) return "이용약관 및 개인정보 처리방침에 동의해주세요.";
    const p = person1;
    if (!p.name.trim()) return "이름을 입력해주세요.";
    const { year: yr, month: mo, day: dy } = parseBirthDate(p.birth_date);
    if (!yr || yr < 1900 || yr > 2020) return "생년월일을 올바르게 입력해주세요. 예: 1990/05/15";
    if (!mo || mo < 1 || mo > 12) return "생월을 올바르게 입력해주세요.";
    if (!dy || dy < 1 || dy > 31) return "생일을 올바르게 입력해주세요.";
    if (!email.trim()) return "결과를 받을 이메일 주소를 입력해주세요.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return "올바른 이메일 주소를 입력해주세요.";
    if (svc?.needPartner) {
      const p2 = person2;
      if (!p2.name.trim()) return "상대방 이름을 입력해주세요.";
      const { year: yr2, month: mo2, day: dy2 } = parseBirthDate(p2.birth_date);
      if (!yr2 || yr2 < 1900 || yr2 > 2020) return "상대방 생년월일을 올바르게 입력해주세요.";
      if (!mo2 || mo2 < 1 || mo2 > 12) return "상대방 생월을 올바르게 입력해주세요.";
      if (!dy2 || dy2 < 1 || dy2 > 31) return "상대방 생일을 올바르게 입력해주세요.";
      if (svc?.ageLimit) {
        const now = new Date().getFullYear();
        if (now - yr < 20 || now - yr2 < 20) return "연애궁합은 만 20세 이상만 이용 가능합니다.";
      }
    }
    return null;
  }

  async function callAnalyze(impUid?: string, merchantUid?: string) {
    setPaying(true);
    try {
      const p1d = parseBirthDate(person1.birth_date);
      const body: any = {
        service_type: svcKey,
        email: email.trim(),
        imp_uid: impUid,
        merchant_uid: merchantUid,
        person1: {
          name: person1.name,
          gender: person1.gender,
          birth_year:   p1d.year,
          birth_month:  p1d.month,
          birth_day:    p1d.day,
          birth_hour:   person1.birth_time_unknown ? 99 : parseTime(person1.birth_time).hour,
          birth_minute: person1.birth_time_unknown ? 0 : parseTime(person1.birth_time).minute,
          birth_type:   person1.birth_type,
          city:         person1.city,
        },
      };
      if (svc?.needPartner) {
        const p2d = parseBirthDate(person2.birth_date);
        body.person2 = {
          name: person2.name,
          gender: person2.gender,
          birth_year:   p2d.year,
          birth_month:  p2d.month,
          birth_day:    p2d.day,
          birth_hour:   person2.birth_time_unknown ? 99 : parseTime(person2.birth_time).hour,
          birth_minute: person2.birth_time_unknown ? 0 : parseTime(person2.birth_time).minute,
          birth_type:   person2.birth_type,
          city:         person2.city,
        };
      }
      const res = await axios.post(`${API_BASE}/api/v1/quick/analyze`, body);
      setResult({
        emailSent:      res.data.email_sent === true,
        reportHtml:     res.data.report_html || "",
        consultationId: res.data.consultation_id ?? null,
      });
    } catch (e: any) {
      setErr(e?.response?.data?.detail || "분석 중 오류가 발생했습니다.");
      setPaying(false);
    }
  }

  function handlePay() {
    setErr("");
    const e = validateForm();
    if (e) { setErr(e); return; }

    if (!svc) return;

    const IMP = (window as any).IMP;
    if (!IMP) {
      // 결제 모듈 없으면 직접 처리 (개발/테스트)
      callAnalyze();
      return;
    }

    IMP.init(process.env.REACT_APP_IMP_CODE || "imp00000000");
    const merchantUid = `quick_${svcKey.replace("퀵사주_", "")}_${Date.now()}`;
    IMP.request_pay(
      {
        pg: "html5_inicis",
        pay_method: "card",
        merchant_uid: merchantUid,
        name: `우연 쿠키사주 — ${svc.label}`,
        amount: svc.price,
        buyer_name: person1.name,
        buyer_email: email.trim(),
      },
      (rsp: any) => {
        if (rsp.success) {
          callAnalyze(rsp.imp_uid, rsp.merchant_uid);
        } else {
          setErr(`결제 실패: ${rsp.error_msg || "다시 시도해주세요."}`);
        }
      }
    );
  }

  const accentColor = svc?.color || "#7048a8";

  /* ── 분석 중 ─────────────────────────────────────────────── */
  if (paying && !result) {
    return (
      <>
        <Global />
        <Wrap>
          <div style={{ textAlign: "center", padding: "100px 16px" }}>
            <div style={{ fontSize: "2.5rem", marginBottom: 16 }}>🍪</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#2d1f3d", marginBottom: 8 }}>
              사주 분석 중...
            </div>
            <div style={{ fontSize: 13, color: "#7a6a8a" }}>
              명리학으로 운세를 풀고 있습니다. 잠시만 기다려주세요.
            </div>
          </div>
        </Wrap>
      </>
    );
  }

  /* ── 결과 화면 ───────────────────────────────────────────── */
  if (result) {
    return (
      <>
        <Global />
        <Wrap>
          <NavBar>
            <NavBtn onClick={() => navigate("/")}>← 홈</NavBtn>
            <PageTitle>🍪 쿠키사주</PageTitle>
          </NavBar>

          <HeaderBox>
            <div style={{ fontSize: "2rem", marginBottom: 6 }}>🍪</div>
            <div style={{ fontSize: "1.4rem", fontWeight: 900, color: "#2d1f3d" }}>분석 완료!</div>
          </HeaderBox>

          <ResultCard>
            {result.emailSent && (
              <div style={{
                display: "flex", alignItems: "center", gap: 10,
                background: `${accentColor}12`, border: `1.5px solid ${accentColor}35`,
                borderRadius: 14, padding: "12px 16px", marginBottom: 16, textAlign: "left",
              }}>
                <span style={{ fontSize: "1.3rem" }}>📩</span>
                <div style={{ fontSize: 13, color: "#2d1f3d", lineHeight: 1.6 }}>
                  <strong style={{ color: accentColor }}>{email}</strong>으로 결과지가 발송되었습니다.<br />
                  <span style={{ fontSize: 11, color: "#9a8aaa" }}>메일이 안 보이면 스팸 폴더를 확인하세요.</span>
                </div>
              </div>
            )}

            {result.reportHtml ? (
              <iframe
                srcDoc={result.reportHtml}
                title="쿠키사주 결과"
                style={{
                  width: "100%", border: "none", borderRadius: 16,
                  minHeight: 700, display: "block",
                  boxShadow: "0 4px 24px rgba(74,32,96,0.1)",
                  marginBottom: 16,
                }}
                onLoad={e => {
                  const iframe = e.currentTarget;
                  try {
                    const h = iframe.contentDocument?.documentElement?.scrollHeight;
                    if (h && h > 100) iframe.style.height = h + 40 + "px";
                  } catch { /* cross-origin 무시 */ }
                }}
              />
            ) : (
              <div style={{ fontSize: 15, fontWeight: 800, color: "#2d1f3d", padding: "24px 0" }}>
                분석이 완료되었습니다!<br />
                <span style={{ fontSize: 13, fontWeight: 400, color: "#7a6a8a" }}>
                  결과가 이메일로 발송되었습니다.
                </span>
              </div>
            )}

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => { setResult(null); setStep(0); setSvcKey(""); setPaying(false); setErr(""); }}
                style={{
                  flex: 1, padding: "13px",
                  background: "#fff", border: "1.5px solid #d8cfe8",
                  borderRadius: 14, fontSize: 14, fontWeight: 700,
                  color: "#5a4a72", cursor: "pointer", fontFamily: "inherit",
                }}>
                다른 운세 보기
              </button>
              <button
                onClick={() => navigate("/client")}
                style={{
                  flex: 1, padding: "13px",
                  background: `linear-gradient(135deg,${accentColor},${accentColor}cc)`,
                  border: "none", borderRadius: 14, fontSize: 14, fontWeight: 800,
                  color: "#fff", cursor: "pointer", fontFamily: "inherit",
                }}>
                상세 사주 보기
              </button>
            </div>
          </ResultCard>
          <SiteFooter />
        </Wrap>
      </>
    );
  }

  return (
    <>
      <Global />
      <Wrap>
        {/* 네비게이션 */}
        <NavBar>
          <NavBtn onClick={() => navigate("/")}>← 홈</NavBtn>
          {step === 1 && (
            <NavBtn onClick={() => { setStep(0); setErr(""); }}>↩ 서비스 선택</NavBtn>
          )}
          <PageTitle>🍪 쿠키사주</PageTitle>
        </NavBar>

        {/* 헤더 */}
        <HeaderBox>
          <div style={{ fontSize: "2rem", marginBottom: 6 }}>🍪</div>
          <div style={{ fontSize: "1.4rem", fontWeight: 900, color: "#2d1f3d" }}>쿠키사주</div>
          <div style={{ fontSize: "0.8rem", color: "#7a6a8a", marginTop: 4 }}>
            궁금한 운세 하나를 딱 골라 명리학으로 풀어드립니다
          </div>
        </HeaderBox>

        {/* ── STEP 0: 서비스 선택 ── */}
        {step === 0 && (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#7a6a8a", textAlign: "center", marginBottom: 14 }}>
              궁금한 운세를 선택하세요
            </div>
            <CardGrid>
              {SERVICES.map((s, idx) => (
                <SvcCard key={s.key} $color={s.color} $idx={idx} onClick={() => selectService(s.key)}>
                  <SvcIcon>{s.icon}</SvcIcon>
                  <SvcLabel $color={s.color}>{s.label}</SvcLabel>
                  <SvcDesc $color={s.color}>{s.desc}</SvcDesc>
                  <SvcPrice $color={s.color}>{s.price.toLocaleString()}원 →</SvcPrice>
                </SvcCard>
              ))}
            </CardGrid>
            <SiteFooter />
          </>
        )}

        {/* ── STEP 1: 입력 폼 ── */}
        {step === 1 && svc && (
          <FormArea>
            {/* 선택된 서비스 배지 */}
            <ServiceBadge $color={svc.color}>
              <span style={{ fontSize: "1.5rem" }}>{svc.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 900, color: svc.color, fontSize: 14 }}>
                  쿠키사주 {svc.label} — {svc.price.toLocaleString()}원
                </div>
                {svc.ageLimit && (
                  <div style={{ fontSize: 11, color: "#e03131", fontWeight: 700, marginTop: 2 }}>
                    ※ 만 20세 이상만 이용 가능
                  </div>
                )}
              </div>
            </ServiceBadge>

            {/* 내 정보 폼 */}
            <PersonFormBlock
              title="내 정보"
              data={person1}
              onChange={setPerson1}
              accentColor={svc.color}
              email={email}
              onEmailChange={setEmail}
            />

            {/* 상대방 정보 (연애궁합만) */}
            {svc.needPartner && (
              <>
                <div style={{ textAlign: "center", fontSize: 12, color: "#9a8aaa", margin: "4px 0 12px", fontWeight: 600 }}>
                  ↕ 상대방 정보도 입력해주세요
                </div>
                <PersonFormBlock
                  title="상대방 정보"
                  data={person2}
                  onChange={setPerson2}
                  accentColor={svc.color}
                />
              </>
            )}

            {/* 이용약관 동의 */}
            <Card style={{ marginTop: 0 }}>
              <ConsentBox>
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={e => setConsent(e.target.checked)}
                />
                <span>
                  <a href="/legal/terms" target="_blank" rel="noreferrer" style={{ color: "#9c7b5a" }}>이용약관</a>
                  {" "}및{" "}
                  <a href="/legal/privacy" target="_blank" rel="noreferrer" style={{ color: "#9c7b5a" }}>개인정보 처리방침</a>
                  에 동의하며, 개인정보(이름, 생년월일)를 사주 분석 목적으로 수집·이용하는 것에 동의합니다. (필수)
                </span>
              </ConsentBox>
            </Card>

            {err && <ErrMsg>{err}</ErrMsg>}

            <SubmitBtn $color={svc.color} onClick={handlePay}>
              🔮 &nbsp;{svc.price.toLocaleString()}원 결제하고 {svc.label} 보기
            </SubmitBtn>
            <div style={{ fontSize: 11, color: "#9a8aaa", textAlign: "center", marginTop: 8 }}>
              결제 완료 즉시 입력하신 이메일로 결과지가 발송됩니다
            </div>
          </FormArea>
        )}
      </Wrap>
    </>
  );
}
